import PropTypes from "prop-types"
import React, {
  Fragment,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react"
import { Controller } from "react-hook-form"

import Button from "src/components/Button"
import Divider from "src/components/Divider"
import FileUpload from "src/components/FileUpload"
import Heading from "src/components/Heading"

import ActiveStorageDirectUpload from "src/utils/form/active_storage_direct_upload"

import Form from "../../../../components/Form"
import ReachOut from "../ReachOut"
import { SignContractWizardContext } from "../SignContractWizardContext"
import { isPhoneRefusal } from "../submitRefusal"
import { evaluateAdditionalStep } from "./additionalStepValidation"
import fileUploadCopy from "./fileUploadCopy"
import { MAX_FILE_BYTES, MAX_FILE_MB } from "./fileUploadLimits"

const FILE_ACCEPT = "image/jpg, image/jpeg, image/png, application/pdf"
const ACCEPTED_TYPES = FILE_ACCEPT.split(",").map((type) => type.trim())
const ACCEPTED_EXTENSIONS = /\.(pdf|jpe?g|png)$/i

const acceptedFile = (file) =>
  ACCEPTED_TYPES.includes(file.type) ||
  (file.type === "" && ACCEPTED_EXTENSIONS.test(file.name))

const directUpload = (file) =>
  new Promise((resolve, reject) => {
    new ActiveStorageDirectUpload(file).uploadFile({
      successCallback: (blob) =>
        resolve({ valid: true, signedId: blob.signed_id }),
      errorCallback: reject,
    })
  })

const checkAndUpload = (wizardCopy) => (file) => {
  if (!acceptedFile(file)) {
    return Promise.resolve({
      valid: false,
      error: wizardCopy.fileTypeUnsupported,
    })
  }
  if (file.size > MAX_FILE_BYTES) {
    return Promise.resolve({
      valid: false,
      error: wizardCopy.fileTooLarge.replace("{{limit}}", MAX_FILE_MB),
    })
  }
  return directUpload(file)
}

const AdditionalInformation = ({ form }) => {
  const { agreementOverview } = useContext(SignContractWizardContext)

  if (agreementOverview) return <AdditionalInformationRedesign form={form} />

  return <AdditionalInformationLegacy form={form} />
}

AdditionalInformation.propTypes = {
  form: PropTypes.object.isRequired,
}

const FieldMessage = ({ message }) =>
  message ? <p className="mt-2 font-semibold text-red-600">{message}</p> : null

FieldMessage.propTypes = {
  message: PropTypes.string,
}

const AdditionalInformationRedesign = ({ form }) => {
  const {
    state,
    dispatch,
    quote,
    marina,
    reachOut,
    contractChatUrl,
    wizardCopy,
    steps,
    customFieldDefinitions,
  } = useContext(SignContractWizardContext)
  const { additionalInformation, additionalInformationFiles, submitFailure } =
    state

  const nextStep = steps[state.currentStep]

  const {
    register,
    control,
    formState: { touchedFields },
  } = form

  const [attemptedContinue, setAttemptedContinue] = useState(false)
  const [replacing, setReplacing] = useState({})
  const [fileErrors, setFileErrors] = useState({})

  const uploadCopy = useMemo(
    () => ({
      ...fileUploadCopy(wizardCopy),
      validating: wizardCopy.uploadingFile,
    }),
    [wizardCopy]
  )
  const onValidate = useMemo(() => checkAndUpload(wizardCopy), [wizardCopy])

  const { valid, missingFieldIds, phoneValid } = useMemo(
    () => evaluateAdditionalStep(state, quote, { includePhone: true }),
    [state, quote]
  )

  const fields = useMemo(
    () =>
      (quote.contractSigningEnablements || [])
        .map(
          ({ customFieldDefinitionId }) =>
            customFieldDefinitions[customFieldDefinitionId]
        )
        .sort((a, b) => a.displayOrder - b.displayOrder),
    [quote.contractSigningEnablements, customFieldDefinitions]
  )

  const requiredMessage = (label) =>
    wizardCopy.requiredField.replace("{{label}}", label)

  const showMessage = (inputKey) =>
    attemptedContinue || Boolean(touchedFields[inputKey])

  const handleTextChange = (onChange, id) => (event) => {
    onChange(event)
    dispatch({
      type: "ADDITIONAL_INFORMATION_CHANGED",
      payload: { [id]: event.target.value },
    })
  }

  const acceptFile = (id) => (file, result) => {
    setReplacing((current) => ({ ...current, [id]: false }))
    setFileErrors((current) => ({ ...current, [id]: null }))
    dispatch({
      type: "ADDITIONAL_INFORMATION_FILE_ATTACHED",
      payload: {
        id,
        signedId: result.signedId,
        name: file.name,
        size: file.size,
      },
    })
  }

  const rejectFile = (id) => (error) =>
    setFileErrors((current) => ({ ...current, [id]: error }))

  const replaceFile = (id) => () => {
    setReplacing((current) => ({ ...current, [id]: true }))
    dispatch({ type: "ADDITIONAL_INFORMATION_FILE_REMOVED", payload: id })
  }

  const handleContinue = () => {
    setAttemptedContinue(true)
    if (valid) dispatch({ type: "GO_TO_NEXT_STEP" })
  }

  const handleEnter = (event) => {
    if (event.key !== "Enter" || event.target.tagName !== "INPUT") return
    event.preventDefault()
    handleContinue()
  }

  const phoneMessage = isPhoneRefusal(submitFailure)
    ? submitFailure.message
    : !phoneValid && showMessage("phone")
    ? wizardCopy.invalidPhone
    : null

  const renderField = ({ id, key, title, required, inputType }) => {
    const missing = missingFieldIds.includes(id)

    if (inputType === "file") {
      return (
        <div key={key}>
          <Form.Label required={required} optional={!required}>
            {title}
          </Form.Label>
          <FileUpload
            label={wizardCopy.uploadField.replace("{{label}}", title)}
            accept={FILE_ACCEPT}
            copy={uploadCopy}
            value={additionalInformationFiles[id] || null}
            error={
              fileErrors[id] ||
              (attemptedContinue && missing ? requiredMessage(title) : null)
            }
            replacing={replacing[id]}
            onValidate={onValidate}
            onAccepted={acceptFile(id)}
            onRejected={rejectFile(id)}
            onReplace={replaceFile(id)}
          />
        </div>
      )
    }

    const { onChange, onBlur, name, ref } = register(key, {
      value: additionalInformation[id] || "",
    })
    const message = missing && showMessage(key) ? requiredMessage(title) : null

    return (
      <div key={key}>
        <Form.Label htmlFor={key} required={required} optional={!required}>
          {title}
        </Form.Label>
        <Form.TextField
          id={key}
          name={name}
          ref={ref}
          onChange={handleTextChange(onChange, id)}
          onBlur={onBlur}
          hasErrors={Boolean(message)}
        />
        <FieldMessage message={message} />
      </div>
    )
  }

  return (
    <div className="flex flex-col space-y-6">
      <div>
        <Heading.HeroTitle>{wizardCopy.additionalTitle}</Heading.HeroTitle>
        <p className="text-base font-semibold text-gray-700">
          {wizardCopy.additionalSubtitle}
        </p>
      </div>

      <div className="hidden lg:block [&>hr]:my-0">
        <Divider />
      </div>

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
        <div
          className="flex flex-col space-y-6 lg:col-span-2"
          onKeyDown={handleEnter}
        >
          {quote.phoneInvalid && (
            <div>
              <Form.Label htmlFor="phone" required>
                {wizardCopy.phoneLabel}
              </Form.Label>
              <Controller
                name="phone"
                control={control}
                defaultValue={state.requiredInformation.phone}
                render={({ field: { onChange, onBlur, name } }) => (
                  <Form.TelephoneField
                    id="phone"
                    name={name}
                    value={state.requiredInformation.phone}
                    onBlur={onBlur}
                    onChange={(phone) => {
                      onChange(phone)
                      dispatch({
                        type: "REQUIRED_INFORMATION_CHANGED",
                        payload: { phone },
                      })
                    }}
                    hasErrors={Boolean(phoneMessage)}
                  />
                )}
              />
              <FieldMessage message={phoneMessage} />
            </div>
          )}

          {fields.map(renderField)}

          <Button variant="primary" fullWidth onClick={handleContinue}>
            {nextStep
              ? `${wizardCopy.continueTo} ${nextStep.title}`
              : wizardCopy.continue}
          </Button>
        </div>

        <div className="flex flex-col space-y-4">
          <ReachOut
            reachOut={reachOut}
            contractChatUrl={contractChatUrl}
            marinaId={marina.id}
            marinaName={marina.name}
            quoteId={quote.id}
          />
        </div>
      </div>
    </div>
  )
}

AdditionalInformationRedesign.propTypes = {
  form: PropTypes.object.isRequired,
}

const AdditionalInformationLegacy = ({ form }) => {
  const {
    dispatch,
    state,
    quote: { contractSigningEnablements },
    customFieldDefinitions,
  } = useContext(SignContractWizardContext)

  // Track display file names for fields with uploads
  const [displayFileNames, setDisplayFileNames] = useState({})

  const {
    register,
    formState: { errors },
    setValue,
    trigger,
    unregister,
  } = form

  // This effect initializes display file names for existing uploads
  useEffect(() => {
    const fileFields = contractSigningEnablements
      .map(
        ({ customFieldDefinitionId }) =>
          customFieldDefinitions[customFieldDefinitionId]
      )
      .filter(({ inputType }) => inputType === "file")

    const newDisplayFileNames = { ...displayFileNames }
    let updated = false

    fileFields.forEach(({ id, key }) => {
      const value = state.additionalInformation[id]
      if (value && value !== "" && !displayFileNames[key]) {
        // If we have a value but no display name, set a generic name
        newDisplayFileNames[key] = "File uploaded"
        updated = true
      }
    })

    if (updated) {
      setDisplayFileNames(newDisplayFileNames)
    }
  }, [
    state.additionalInformation,
    contractSigningEnablements,
    customFieldDefinitions,
  ])

  // This effect ensures file inputs are properly registered with React Hook Form
  useEffect(() => {
    // Get all file-type fields
    const fileFields = contractSigningEnablements
      .map(
        ({ customFieldDefinitionId }) =>
          customFieldDefinitions[customFieldDefinitionId]
      )
      .filter(({ inputType }) => inputType === "file")
      .map(({ key, id, title, required }) => ({
        key,
        id,
        label: title,
        required,
        value: state.additionalInformation[id] || "",
      }))

    // Register each file field with the proper validation
    fileFields.forEach(({ key, required, label, value }) => {
      // We need to unregister first to avoid duplicate registrations
      unregister(key)

      // Then register with the appropriate validation
      register(key, {
        required: required ? `${label} is required.` : false,
        validate: (fieldValue) => {
          if (required && (!fieldValue || fieldValue === "")) {
            return `${label} is required.`
          }
          return true
        },
      })

      // Set the initial value if there is one
      if (value) {
        setValue(key, value, { shouldValidate: true })
      }
    })

    // Clean up on unmount
    return () => {
      fileFields.forEach(({ key }) => unregister(key))
    }
  }, [
    register,
    unregister,
    setValue,
    contractSigningEnablements,
    customFieldDefinitions,
    state.additionalInformation,
  ])

  const handleChange = (eventHandler, id) => (event) => {
    eventHandler(event)

    const payload = { [id]: event.target.value }

    dispatch({
      type: "ADDITIONAL_INFORMATION_CHANGED",
      payload,
    })
  }

  const handleFileUploadSuccess = (id, inputKey) => (blob) => {
    // Update the Redux-like state
    const payload = { [id]: blob.signed_id }

    dispatch({
      type: "ADDITIONAL_INFORMATION_CHANGED",
      payload,
    })

    // Also update React Hook Form's state to clear the required validation error
    setValue(inputKey, blob.signed_id, {
      shouldValidate: true,
      shouldDirty: true,
    })

    // Store the file name for display
    setDisplayFileNames({
      ...displayFileNames,
      [inputKey]: blob.filename || "File uploaded",
    })

    // Trigger validation to immediately clear any errors
    trigger(inputKey)
  }

  const handleFileRemove = (id, inputKey) => () => {
    // Clear the Redux-like state
    const payload = { [id]: "" }

    dispatch({
      type: "ADDITIONAL_INFORMATION_CHANGED",
      payload,
    })

    // Also update React Hook Form's state
    setValue(inputKey, "", {
      shouldValidate: true,
      shouldDirty: true,
    })

    // Remove the file name from display
    const newDisplayFileNames = { ...displayFileNames }
    delete newDisplayFileNames[inputKey]
    setDisplayFileNames(newDisplayFileNames)

    // Trigger validation to show error again if field is required
    trigger(inputKey)
  }

  return (
    <div className="flex flex-col space-y-4">
      {contractSigningEnablements
        .map(({ customFieldDefinitionId }) => {
          return customFieldDefinitions[customFieldDefinitionId]
        })
        .sort((a, b) => a.displayOrder - b.displayOrder)
        .map(({ key: inputKey, title: label, id, required, inputType }) => {
          const value = state.additionalInformation[id] || ""
          const hasUploadedFile = inputType === "file" && value !== ""
          const fileName = displayFileNames[inputKey] || ""

          const errorsForKey = errors[inputKey]

          // Only set up register for non-file inputs
          // File inputs are registered in the useEffect
          let registerProps = {}
          if (inputType !== "file") {
            const inputOptions = {
              value: value,
            }

            if (required) {
              inputOptions.required = `${label} is required.`
            }

            registerProps = register(inputKey, inputOptions)
          } else {
            // For file inputs, we still need name and ref, but not onChange which will be handled by the component
            registerProps = {
              name: inputKey,
              ref: register(inputKey).ref,
              onBlur: register(inputKey).onBlur,
            }
          }

          return (
            <Fragment key={inputKey}>
              <Form.Label
                htmlFor={inputKey}
                required={required}
                optional={!required}
              >
                {label}
              </Form.Label>
              <div className="flex flex-col space-y-2">
                {inputType === "file" ? (
                  <Form.DirectUploadFileInput
                    id={inputKey}
                    name={registerProps.name}
                    ref={registerProps.ref}
                    accept="image/jpg, image/jpeg, image/png, application/pdf"
                    required={required}
                    hasErrors={Boolean(errorsForKey)}
                    onBlur={registerProps.onBlur}
                    onUploadSuccess={handleFileUploadSuccess(id, inputKey)}
                    onFileRemove={handleFileRemove(id, inputKey)}
                    placeholder={`Upload ${label}`}
                    fileName={hasUploadedFile ? fileName : ""}
                  />
                ) : (
                  <Form.TextField
                    id={inputKey}
                    onChange={handleChange(registerProps.onChange, id)}
                    onBlur={registerProps.onBlur}
                    name={registerProps.name}
                    ref={registerProps.ref}
                    hasErrors={Boolean(errorsForKey)}
                  />
                )}
                <div className="font-semibold text-red-600">
                  {errorsForKey ? errorsForKey.message : <span>&nbsp;</span>}
                </div>
              </div>
            </Fragment>
          )
        })}
    </div>
  )
}

AdditionalInformationLegacy.propTypes = {
  form: PropTypes.object.isRequired,
}

export default AdditionalInformation
